import { Tooltip } from 'antd';
import { useCallback, useState } from 'react';
import styled from 'styled-components';

import { ArrowLeftSvg } from '../../../assets/icons';
import theme from '../../../assets/theme';
import {
  selectedSlideSelector,
  TemplateStateActions,
  useDesignerDataContext,
} from '../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  GeneratorTabState,
  useDesignerUIContext,
} from '../../../hooks/useDesignerUIState';
import { useMetrics } from '../../../hooks/useMetrics';
import { RoundMedia } from '../../../services/backendService/types';
import { zIndexes } from '../../../utils/zIndexes';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { Slide } from '../../SlideComponents/slide.types';
import AttachmentsTab from './AttachmentsTab';
import BackgroundTab from './BackgroundTab';
import ColorPickerTab from './ColorPickerTab';
import EditAiFeedbackTab from './EditAiFeedbackTab';
import FigureElementTab from './FigureElementTab';
import GeneratorsTab from './GeneratorsTab';
import InteractiveElementsTab from './InteractiveElements';
import MediaTab from './MediaTab';
import PromptsTab from './PromptsTab';
import TemplatesTab from './TemplatesTab';
import TestCasesResultTab from './TestCasesResultTab';
import TestCasesTab from './TestCasesTab';
import TextTab from './TextTab';
import TranslateTab from './TranslateTab';
import UploadsTab from './UploadsTab';

//TODO: rename rounds variables to slide variables
const DesignerTabPane = ({
  templateId,
  setGeneratorTabState,
  setUploadedSlidesCache,
}: {
  templateId: string;
  setGeneratorTabState: (generatorTabState: GeneratorTabState) => void;
  setUploadedSlidesCache: (rounds: Slide[] | undefined) => void;
}) => {
  const metrics = useMetrics();
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const dataDispatch = useDesignerDataContext(context => context.dispatch);

  const selectedElements = useDesignerUIContext(
    context => context.state.selectedElements,
  );

  const setSelectedElements = useDesignerUIContext(
    context => context.setSelectedElements,
  );
  const onRoundChange = useDesignerDataContext(context => context.handleSlideChange);
  const selectedSlide = useDesignerDataContext(selectedSlideSelector);
  const onElementsChange = useDesignerDataContext(
    context => context.handleElementsChange,
  );
  const onAddSlide = useDesignerDataContext(context => context.addSlide);
  const addTranslatedSlides = useDesignerDataContext(
    context => context.addTranslatedSlides,
  );

  const onAllRoundChange = useDesignerDataContext(
    context => context.handleAllSlideChange,
  );

  const lessonAttachments = useDesignerDataContext(
    context => context.state.localTemplate?.attachments,
  );

  const aiContent = useDesignerDataContext(c => c.state.localTemplate?.aiContent);
  const handleCreateNewSlide = useDesignerDataContext(
    context => context.handleCreateNewSlide,
  );
  const handleSlideTypeChanged = useDesignerDataContext(
    context => context.handleSlideTypeChanged,
  );
  const isLeftTabCollapsed = useDesignerUIContext(c => c.state.isLeftTabCollapsed);
  const rounds = useDesignerDataContext(
    context => context.state.localTemplate?.content.slides || [],
  );

  const setBackgroundForAllRounds = useCallback(
    (media: RoundMedia) => {
      dataDispatch({ type: TemplateStateActions.setBackgroundOnAllSlides, media });
    },
    [dataDispatch],
  );

  const selectedTab = useDesignerUIContext(context => context.state.selectedTab);

  const [searchTemplatesValue, setSearchTemplatesValue] = useState<string | undefined>();

  const handleAIGeneratedRoundCreated = useCallback(
    (slides: Slide[]) => {
      onAddSlide(slides);
    },
    [onAddSlide],
  );

  const handleClearDroppedImageData = useCallback(() => {
    dispatch({
      type: DesignerUIStateActions.clearDroppedImageData,
    });
  }, [dispatch]);
  if (isLeftTabCollapsed) {
    return (
      <FloatingContainerWrapper $isLeftTabCollapsed={isLeftTabCollapsed}>
        <FloatingContainer></FloatingContainer>
      </FloatingContainerWrapper>
    );
  }
  return (
    <FloatingContainerWrapper $isLeftTabCollapsed={isLeftTabCollapsed}>
      <FloatingContainer>
        {selectedTab.tab === 'generators' && (
          <GeneratorsTab
            onAddActivity={handleAIGeneratedRoundCreated}
            selectedSubTab={selectedTab.subTab}
            handleCreateNewSlide={handleCreateNewSlide}
            setGeneratorTabState={setGeneratorTabState}
          />
        )}
        {selectedTab.tab === 'templates' && (
          <TemplatesTab
            onAddActivity={onAddSlide}
            setSearchTemplatesValue={setSearchTemplatesValue}
            searchTemplatesValue={searchTemplatesValue}
            templateId={templateId}
          />
        )}
        {selectedTab.tab === 'interactiveElements' && (
          <InteractiveElementsTab
            handleRoundTypeChanged={handleSlideTypeChanged}
            onSlideChange={onRoundChange}
            rounds={rounds ? rounds : []}
            slide={selectedSlide}
            isGenerator={!!aiContent?.prompts?.length}
            promptInputs={aiContent?.promptInputs}
            templateId={templateId}
            onAllRoundChange={onAllRoundChange}
            setSelectedElements={setSelectedElements}
          />
        )}
        {selectedTab.tab === 'background' && (
          <BackgroundTab
            onRoundChange={onRoundChange}
            round={selectedSlide}
            setBackgroundForAllRounds={setBackgroundForAllRounds}
          />
        )}
        {selectedTab.tab === 'text' && (
          <TextTab onRoundChange={onRoundChange} selectedSlide={selectedSlide} />
        )}
        {selectedTab.tab === 'uploads' && (
          <UploadsTab
            onAddActivity={onAddSlide}
            templateId={templateId}
            setUploadedSlidesCache={setUploadedSlidesCache}
          />
        )}

        {selectedTab.tab === 'media' && (
          <MediaTab
            templateId={templateId}
            onRoundChange={onRoundChange}
            round={selectedSlide}
            tabState={selectedTab}
            selectedElements={selectedElements}
            droppedImageData={selectedTab.initialData?.droppedImageData}
            onDroppedImageDataConsumed={handleClearDroppedImageData}
            onElementsChange={onElementsChange}
          />
        )}
        {selectedTab.tab === 'elements' && (
          <FigureElementTab onRoundChange={onRoundChange} round={selectedSlide} />
        )}
        {selectedTab.tab === 'translate' && (
          <TranslateTab
            selectedSlide={selectedSlide}
            slides={rounds}
            onAddTranslatedSlides={addTranslatedSlides}
            lessonAttachments={lessonAttachments}
          />
        )}
        {selectedTab.tab === 'prompts' && (
          <PromptsTab
            prompts={aiContent?.prompts}
            promptInputs={aiContent?.promptInputs}
          />
        )}

        {selectedTab.tab === 'testCases' && <TestCasesTab />}
        {selectedTab.tab === 'testCasesResult' && <TestCasesResultTab />}
        {selectedTab.tab === 'colorPicker' && (
          <ColorPickerTab
            onElementsChange={onElementsChange}
            onRoundChange={onRoundChange}
            selectedElements={selectedElements}
            tabState={selectedTab}
            round={selectedSlide}
          />
        )}
        {selectedTab.tab === 'editAiFeedback' && selectedSlide && (
          <EditAiFeedbackTab
            onSlideChange={onRoundChange}
            slide={selectedSlide}
            isGenerator={!!aiContent?.prompts?.length}
            promptInputs={aiContent?.promptInputs}
            presentationId={templateId}
          />
        )}
        {selectedTab.tab === 'attachments' && <AttachmentsTab />}
      </FloatingContainer>
      {!isLeftTabCollapsed && (
        <CollapseButtonContainer
          onClick={() => {
            metrics.logEvent('Designer.LeftTabCollapsed');
            dispatch({
              type: DesignerUIStateActions.setLeftTabCollapsed,
              isCollapsed: true,
            });
          }}
        >
          <Tooltip title="Hide left tab">
            <CollapseButton>
              <IconWrapper>
                <ArrowLeftSvg />
              </IconWrapper>
            </CollapseButton>
          </Tooltip>
        </CollapseButtonContainer>
      )}
    </FloatingContainerWrapper>
  );
};

export default DesignerTabPane;

const FloatingContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: white;
  display: flex;
  flex-direction: column;
  border-radius: ${theme.effects.floatingCreatorElementBorderRadius};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  overflow-y: scroll;
  overflow-y: scroll;
  z-index: ${zIndexes('CREATOR_LEFT_BAR')};
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;
const FloatingContainerWrapper = styled.div<{ $isLeftTabCollapsed: boolean }>`
  width: 100%;
  margin-left: ${({ $isLeftTabCollapsed }) => ($isLeftTabCollapsed ? '0px' : '5px')};
  background-color: transparent;
  z-index: ${zIndexes('CREATOR_LEFT_BAR')};
  padding: 5px;
  height: calc(100vh - 60px);
  padding-right: 0;
  display: grid;
  grid-template-columns: auto 10px;
  position: relative;
`;

const CollapseButtonContainer = styled.div`
  padding: 0;
  border: none;
  justify-content: center;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: 10px;
  z-index: ${zIndexes('CREATOR_LEFT_BAR_HIDE')};
  svg {
    margin: 0;
  }
`;

const CollapseButton = styled.div`
  background-color: white;
  height: 80px;
  display: flex;
  padding: 5px;
  border-radius: 0 10px 10px 0;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  svg {
    margin: 0;
    width: 20px !important;
    height: 20px !important;
  }
`;
